"use client";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_components/ui/alert-dialog";
import { Button } from "@/app/_components/ui/button";
import { useIsIdleLogoffPaused } from "@/lib/hooks/useIdleLogoffPause";
import {
  IDLE_ACTIVITY_EVENTS,
  IDLE_ACTIVITY_UPDATE_KEY,
  IDLE_LOGOFF_WARNING_S,
  IDLE_ACTIVITY_REPORT_INTERVAL_S,
  type IdleLogoffSessionState,
} from "@/lib/modules/auth/idle-logoff";
import getLogger from "@/lib/logger";
import type { Session } from "next-auth";
import { getCsrfToken, signOut, useSession } from "next-auth/react";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = getLogger("idle-logoff");
const SESSION_URL = "/api/auth/session";
const REPORT_INTERVAL_MS = IDLE_ACTIVITY_REPORT_INTERVAL_S * 1000;
// How often the warning re-reads the deadline other tabs may have moved.
const WARNING_RECHECK_MS = 30_000;

/**
 * The client half of the staff idle logoff. The server decides when a
 * session is idle; this reports real input to it every few minutes, warns
 * before the limit, and reloads the page once it passes so middleware can
 * send the user to sign in and back. Renders nothing for sessions the logoff
 * doesn't apply to.
 */
export function IdleLogoffMonitor() {
  const { data: session, status } = useSession();
  const idleLogoff = session?.idleLogoff;
  const hadIdleLogoffRef = useRef(false);

  // Any session refetch (window focus, another component) can be the one
  // that finds the server logged this tab out. Reload so middleware sends
  // them to sign in, instead of leaving a dead page up. The delay lets a
  // deliberate signOut() navigate away first.
  useEffect(() => {
    if (idleLogoff) {
      hadIdleLogoffRef.current = true;
      return;
    }
    if (!hadIdleLogoffRef.current || status === "loading" || session?.user) {
      return;
    }
    const timeout = setTimeout(() => window.location.reload(), 1000);
    return () => clearTimeout(timeout);
  }, [idleLogoff, session?.user, status]);

  if (!idleLogoff) {
    return null;
  }
  return <ActiveIdleLogoffMonitor initialState={idleLogoff} />;
}

function ActiveIdleLogoffMonitor({
  initialState,
}: {
  initialState: IdleLogoffSessionState;
}) {
  const isPaused = useIsIdleLogoffPaused();
  const timeoutS = initialState.timeoutS;

  // Deadline and timestamps on this tab's clock. The server's clock only
  // reaches the client as "expires in", so skew between them doesn't matter.
  const deadlineMsRef = useRef(Date.now() + initialState.expiresInS * 1000);
  const lastReportMsRef = useRef(deadlineMsRef.current - timeoutS * 1000);
  const lastInputMsRef = useRef(0);
  const lastCheckMsRef = useRef(0);
  const isBusyRef = useRef(false);
  const [remainingS, setRemainingS] = useState(initialState.expiresInS);

  const applySession = useCallback((next: Session | null) => {
    if (next?.idleLogoff) {
      deadlineMsRef.current = Date.now() + next.idleLogoff.expiresInS * 1000;
    }
    return next;
  }, []);

  // A session refetch from another component (window focus, update()) also
  // carries the latest deadline, including activity from other tabs.
  useEffect(() => {
    applySession({ idleLogoff: initialState } as Session);
  }, [applySession, initialState]);

  const reportActivity = useCallback(async () => {
    lastReportMsRef.current = Date.now();
    try {
      const response = await fetch(SESSION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          csrfToken: await getCsrfToken(),
          data: { [IDLE_ACTIVITY_UPDATE_KEY]: true },
        }),
      });
      applySession(await response.json());
    } catch (error) {
      logger.warn(
        {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        "Failed to report activity for the idle logoff",
      );
    }
  }, [applySession]);

  // Real input only. Polling, refetches and timers never reach this.
  useEffect(() => {
    const handleInput = () => {
      lastInputMsRef.current = Date.now();
      const isWarning =
        deadlineMsRef.current - Date.now() <= IDLE_LOGOFF_WARNING_S * 1000;
      if (
        isWarning ||
        Date.now() - lastReportMsRef.current >= REPORT_INTERVAL_MS
      ) {
        void reportActivity();
      }
    };
    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        handleInput();
      }
    };

    IDLE_ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleInput, { passive: true }),
    );
    window.addEventListener("focus", handleInput);
    document.addEventListener("visibilitychange", handleVisible);
    return () => {
      IDLE_ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleInput),
      );
      window.removeEventListener("focus", handleInput);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [reportActivity]);

  useEffect(() => {
    const tick = async () => {
      if (isBusyRef.current) {
        return;
      }
      const nowMs = Date.now();

      // A live session keeps the server's clock moving with no input at all.
      if (isPaused) {
        if (nowMs - lastReportMsRef.current >= REPORT_INTERVAL_MS) {
          await reportActivity();
        }
        setRemainingS(Math.round((deadlineMsRef.current - nowMs) / 1000));
        return;
      }

      const remainingMs = deadlineMsRef.current - nowMs;
      const isCheckDue =
        remainingMs <= 0 ||
        nowMs - lastCheckMsRef.current >= WARNING_RECHECK_MS;
      if (remainingMs <= IDLE_LOGOFF_WARNING_S * 1000 && isCheckDue) {
        lastCheckMsRef.current = nowMs;
        isBusyRef.current = true;
        try {
          if (lastInputMsRef.current > lastReportMsRef.current) {
            // Input this tab hasn't reported yet still counts.
            await reportActivity();
          } else {
            // Another tab may have reported since this one last heard.
            const response = await fetch(SESSION_URL);
            const latest = applySession(await response.json());
            if (!latest?.idleLogoff) {
              // The server has logged this session out. A reload lets
              // middleware send them to sign in, then back to this page.
              window.location.reload();
              return;
            }
          }
        } catch (error) {
          logger.warn(
            {
              errorMessage:
                error instanceof Error ? error.message : String(error),
            },
            "Failed to check the idle logoff deadline",
          );
        } finally {
          isBusyRef.current = false;
        }
      }
      setRemainingS(Math.round((deadlineMsRef.current - Date.now()) / 1000));
    };

    const interval = setInterval(() => void tick(), 1000);
    return () => clearInterval(interval);
  }, [applySession, isPaused, reportActivity]);

  const isWarningOpen =
    !isPaused && remainingS <= IDLE_LOGOFF_WARNING_S && remainingS >= 0;

  return (
    <AlertDialog open={isWarningOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you still there?</AlertDialogTitle>
          <AlertDialogDescription>
            {`You'll be signed out in ${_formatCountdown(remainingS)} because you've been inactive. This keeps client information private when you step away.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => void signOut({ callbackUrl: "/auth/login" })}
          >
            Sign out
          </AlertDialogCancel>
          <Button onClick={() => void reportActivity()}>Stay signed in</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function _formatCountdown(seconds: number) {
  const clamped = Math.max(0, seconds);
  const minutes = Math.floor(clamped / 60);
  return `${minutes}:${String(clamped % 60).padStart(2, "0")}`;
}
